import { Button, Card, Typography, FormValues, FormObject, useAppForm } from '@stern-ui/ui';
import { useStore } from '@tanstack/react-form';
import { AccountOutline, Information } from 'mdi-material-ui';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import z from 'zod';

import CreateListSvg from '@/assets/create_list.svg';
import {
	SummaryFilter,
	selectRowsTotal,
	isFiltersApplied,
	selectFilterValues,
	FilterValuesObj,
	selectSearchValue,
} from '@/components';
import { addNotification } from '@/components/Notifications/notificationsSlice';
import { createDynamicListFeatureFlagEnabled } from '@/globalConsts/featureFlags';
import { trackEvent } from '@/lib/analytics';

import { CreatePatientListForm, CreatePatientListRequest } from '../../types';

type CreateListFormProps = {
	submitFormAction: (values: CreatePatientListRequest) => void;
};

const resultsLimitMin = 1;
const resultsLimitMax = 5000;

const schema = z.object({
	name: z.string().min(1, 'List name is required'),
	filters: z.custom<FormValues>(),
	search_value: z.union([z.string(), z.undefined()]),
	list_type: z.enum(['static', 'dynamic'], { message: 'List type is required' }),
	max_results: z
		.string()
		.refine((value) => {
			if (!value || value.length === 0) return true;
			return /^[0-9]+$/.test(value);
		}, 'Please enter a valid number')
		.refine((value) => {
			if (!value || value.length === 0) return true;
			const parsed = parseInt(value, 10);
			if (isNaN(parsed)) return true;
			return parsed >= resultsLimitMin && parsed <= resultsLimitMax;
		}, `Please enter a number between ${resultsLimitMin} and ${resultsLimitMax}`),
});

export const CreateListForm = (props: CreateListFormProps) => {
	const { submitFormAction } = props;
	const rowsTotal = useSelector(selectRowsTotal);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const filterValues = useSelector(selectFilterValues);
	const searchValue = useSelector(selectSearchValue);

	const filterValuesFromState = {
		...(filterValues as FormValues),
	} as FormObject;

	const initialValues: CreatePatientListForm = {
		name: '',
		filters: { ...filterValuesFromState },
		search_value: searchValue || undefined,
		list_type: createDynamicListFeatureFlagEnabled ? '' : 'static',
		max_results: '',
	};

	const form = useAppForm({
		defaultValues: initialValues,
		validators: {
			onMount: schema,
			onChange: schema,
		},
		onSubmit: async ({ value }) => {
			// API expects max_results to be part of the filters object, not a top-level property
			const { max_results, ...rest } = value;
			const formValuesForSave: CreatePatientListRequest = {
				...rest,
				filters: {
					...filterValuesFromState,
					max_results,
				},
			};
			await submitFormAction(formValuesForSave);
			trackEvent(`Patient list 'Create list' clicked`);
		},
	});

	const hasFiltersApplied = isFiltersApplied(filterValues as FilterValuesObj);
	const hasSearchApplied = searchValue && searchValue.length > 0;
	const canCreateList = !(
		!rowsTotal ||
		rowsTotal === 0 ||
		!(hasFiltersApplied || hasSearchApplied)
	);

	useEffect(() => {
		if (!canCreateList) {
			// filters have expired, so add notificaton and redirect
			dispatch(
				addNotification({
					type: 'error',
					title: 'Session expired',
					message: 'Your filters have expired, please try again.',
					timeoutLength: 8000,
				})
			);
			navigate('../../', { relative: 'path' });
		}
	}, [canCreateList, dispatch, navigate]);

	const listTypeFormValue = useStore(form.store, (state) => state.values.list_type);
	const maxResultsFormValue = useStore(form.store, (state) => state.values.max_results);
	const maxResultsFieldMeta = useStore(form.store, (state) => state.fieldMeta?.max_results);

	useEffect(() => {
		if (listTypeFormValue === 'dynamic') {
			// reset the max_results field value if the list type is dynamic, as it is not applicable
			form.setFieldValue('max_results', '');
		}
	}, [listTypeFormValue, form]);

	const patientCount = useMemo(() => {
		if (
			listTypeFormValue !== 'dynamic' &&
			maxResultsFormValue &&
			maxResultsFormValue.length > 0 &&
			maxResultsFieldMeta?.isValid
		) {
			const parsed = parseInt(maxResultsFormValue, 10);
			if (rowsTotal && parsed < rowsTotal) {
				return parsed;
			}
		}
		return rowsTotal || 0;
	}, [maxResultsFormValue, listTypeFormValue, rowsTotal, maxResultsFieldMeta?.isValid]);

	return (
		<form
			onSubmit={(e) => {
				e.preventDefault();
				void form.handleSubmit();
			}}
			data-test-id='create-list-form'
		>
			<>
				<div className='grid lg:grid-cols-3 gap-6 mb-8'>
					<Card variant='layoutCard' className='lg:col-span-2 min-h-[calc(100vh-340px)]'>
						<div className='flex flex-col items-center py-16'>
							<img className='w-[124px] mb-5' alt='list icon' src={CreateListSvg} />
							<Typography variant='heading_3_medium' className='mb-1'>
								Save this filter as a patient list
							</Typography>
							<Typography
								variant='body_lg_regular'
								className='mb-8 text-tertiaryTextDark'
							>
								Add a name and choose whether this list should stay fixed or update
								over time.
							</Typography>
							<div className='text-left w-2/3 flex flex-col gap-8'>
								<div data-test-id='create-list-form-name-field'>
									<form.AppField name='name'>
										{(field) => (
											<field.TextField
												type='text'
												label='List name'
												required={true}
											/>
										)}
									</form.AppField>
								</div>
								<div data-test-id='create-list-form-list-type-field'>
									<form.AppField name='list_type'>
										{(field) => (
											<field.RadioField
												groupLabel='List type'
												required={true}
												options={[
													{
														label: 'Static list',
														value: 'static',
														description:
															'Save the current matching patients as a fixed list.',
													},
													{
														label: 'Dynamic list',
														value: 'dynamic',
														description:
															'New patients will be automatically added when they match the saved filter criteria.',
													},
												]}
												variant='card'
												disabled={!createDynamicListFeatureFlagEnabled}
											/>
										)}
									</form.AppField>
								</div>
								<div data-test-id='create-list-form-max-results'>
									<div className='grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8 pb-3'>
										<div className='col-span-1 md:col-span-2'>
											<form.AppField name='max_results'>
												{(field) => (
													<field.TextField
														label='Maximum number of patients'
														inputMode='numeric'
														className='max-w-[50%]'
														disabled={listTypeFormValue === 'dynamic'}
														aria-describedby={
															listTypeFormValue === 'dynamic'
																? 'max-results-disabled-info'
																: undefined
														}
													/>
												)}
											</form.AppField>
										</div>
									</div>
									{listTypeFormValue === 'dynamic' && (
										<div className='inline-flex flex-row pb-3'>
											<Information
												aria-hidden='true'
												fontSize='small'
												className='text-secondaryTextDark'
											/>
											<Typography
												className='text-secondaryTextDark pl-2'
												id='max-results-disabled-info'
											>
												Dynamic lists cannot have a maximum number of
												patients.
											</Typography>
										</div>
									)}
									{listTypeFormValue !== 'dynamic' && (
										<Typography className='text-secondaryTextDark'>
											Leave this blank to return all the patients who match
											your filter criteria.
										</Typography>
									)}
								</div>
							</div>
						</div>
					</Card>
					<Card
						variant='layoutCard'
						className='min-h-[calc(100vh-340px)] overflow-hidden'
					>
						<Typography variant='title2' className='mb-6'>
							<AccountOutline className='mr-2' aria-hidden='true' />
							<span className='text-primaryBrand' data-test-id='patient-count'>
								{patientCount}
							</span>{' '}
							patient
							{patientCount && patientCount > 1 ? 's' : ''} will be added to the list
						</Typography>
						<Typography variant='body_lg_medium' className='mb-4'>
							Patients match these criteria:
						</Typography>
						<div className='overflow-y-auto h-full'>
							<SummaryFilter searchValue={searchValue} isUpdatable={false} />
						</div>
					</Card>
				</div>
				<div className='pt-2 mb-8'>
					<div className='flex flex-row justify-between'>
						<Link to='../'>
							<Button type='button' variant='tertiary'>
								Cancel
							</Button>
						</Link>
						<div className='flex flex-row'>
							<form.AppForm>
								<form.SubmitButton>Create</form.SubmitButton>
							</form.AppForm>
						</div>
					</div>
				</div>
			</>
		</form>
	);
};
